import { difference } from 'lodash';
import { getEnvStage, isEnvStage } from '$util/index';
import type { CollectionPathPlaceholder } from '$cms/types';
import { collectionPathPlaceholders } from '$cms/types';
import type { AnyObject } from '$util/types';

/**
 * Extract placeholder names from a path.
 * eg. '/mot/{area}/{car-make}' -> ['area', 'car-make']
 */
export const getPathPlaceholders = (path: string): CollectionPathPlaceholder[] => {
  const placeholders = Array.from(path.matchAll(/\{([\w-]+)\}/g)).map(([, placeholder]) => placeholder);
  const unknownPlaceholders = difference(placeholders, collectionPathPlaceholders);
  if (unknownPlaceholders.length) {
    throw new TypeError(`Unknown path placeholders: ${unknownPlaceholders.join(', ')}`);
  }
  return placeholders as CollectionPathPlaceholder[];
};

/**
 * The action should skip if the current env stage doesn't match the env tag.
 * No tags will run for all env stages.
 */
export const skipActionForEnvTags = (tags: string[]): boolean => {
  const envTags = ['development', 'staging', 'production'];
  const hasEnvTag = tags.some((tag) => envTags.includes(tag));
  return hasEnvTag && !tags.includes(getEnvStage());
};

/**
 * Preview is active by adding `?preview` to the URL.
 * @note On production it requires NextJs preview mode to also be active.
 * @see /pages/api/preview-page.ts
 */
export const isPreviewActive = (urlQuery: AnyObject = {}, isPreviewMode = false): boolean => {
  if (!('preview' in urlQuery)) return false;
  if (isPreviewMode) return true; // when NextJs preview mode is active
  return !isEnvStage('production');
};
